import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { Dialog, DialogTitle } from '@mui/material';

import { updateDar } from '../../../store/participantService';
import { loadUser } from '../../../store/userService';
import { getObjFromId } from '../../../utils/directusFunctions';
import {
    ADVISER,
    hasRole,
    MANAGER,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
} from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import SingleSelect from '../../formElements/SingleSelect';
import StaticField from '../../formElements/StaticField';
import TextAreaField from '../../formElements/TextAreaField';
import ScreenError from '../../ui/messageBanners/ScreenError';

import { validate } from './darValidation/validateDarChangeEditor';

import form from '../../../commonStyles/formStyles.module.css';
import modal from '../../../commonStyles/modal.module.css';
import local from '../homeStyles/dar.module.css';

const acceptedRoles = [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];
const restrictedRoles = [QUALITY, SUPERUSER];
const NOTES_CHARACTER_LIMIT = 750;
export const message = 'DAR has been updated';

const DarChangeEditor = ({ isOpen, onClose, row }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = {
        id: '',
        statusId: '',
        notes: ''
    };
    const [newEntry, setNewEntry] = useState(initialState);
    const [statuses, setStatuses] = useState([]);
    const [errors, setErrors] = useState({});
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const currentUser = useSelector((state) => state.entities.userService.currentUser);
    const darStatuses = useSelector((state) => state.entities.directusService.darStatuses);
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS
    useEffect(() => {
        if (!row || !('id' in row)) return;
        if (Object.keys(row)?.length > 0) dispatch(loadUser(row.userId));
        setNewEntry((prev) => ({ ...prev, id: row.id }));
    }, [row]);

    useEffect(() => {
        if (darStatuses.length < 1) return;
        const newStatuses = !hasRole(restrictedRoles, roles)
            ? darStatuses.filter((el) => el.name.toLowerCase() !== 'completed')
            : darStatuses;
        setStatuses(newStatuses);
    }, [darStatuses]);

    useEffect(() => {
        setIsButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (successMessage === message) {
            clearData();
            setIsButtonDisabled(false);
        }
    }, [successMessage]);

    // FNs
    const clearError = (key) => {
        if (key in errors && errors[key].error) setErrors((errors) => ({ ...errors, [key]: '' }));
    };

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onCancel = (e, reason) => {
        if (reason === 'backdropClick') return;
        clearData();
    };

    const clearData = () => {
        setNewEntry(initialState);
        setErrors({});
        onClose();
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsButtonDisabled(true);
        const newErrors = validate(newEntry);
        if (Object.values(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        dispatch(updateDar(newEntry, message));
    };

    // RENDER
    return (
        <div>
            <Dialog
                open={isOpen}
                onClose={onCancel}
                aria-labelledby="form-dialog-title"
                className="muiDialogWrapper"
                fullWidth={true}>
                <div className={modal.modalHeader}>
                    <DialogTitle id="form-dialog-title">Update Data Amendment Request</DialogTitle>
                </div>
                <div className={modal.modalContent}>
                    <form
                        onSubmit={onSubmit}
                        className={form.formWrapper}
                        data-testid="dar-change-editor-form">
                        <div className={form.formSection}>
                            <div className={form.formColumn}>
                                <StaticField
                                    id="requester"
                                    label="Requester"
                                    content={currentUser.loginEmailAddress}
                                />
                                <StaticField
                                    id="requestType"
                                    label="Request Type"
                                    content={row?.requestTypeName}
                                />
                            </div>
                            <div className={form.formColumn}>
                                <SingleSelect
                                    id="statusId"
                                    label="Status"
                                    placeholder="Select DAR status..."
                                    mandatory={true}
                                    disabled={!hasRole(acceptedRoles, roles)}
                                    customClass="absolutePosition"
                                    menuItems={statuses || []}
                                    selectedId={newEntry.statusId || ''}
                                    selected={getObjFromId(statuses, newEntry.statusId)}
                                    error={errors.statusId}
                                    onChange={(chosenId) => onUpdate('statusId', chosenId)}
                                />
                            </div>
                        </div>
                        <TextAreaField
                            id="notes"
                            label="Notes"
                            placeholder="Enter reversion notes"
                            mandatory={true}
                            disabled={!hasRole(acceptedRoles, roles)}
                            maxLength={NOTES_CHARACTER_LIMIT}
                            value={newEntry.notes}
                            count={`${newEntry.notes.length}/${NOTES_CHARACTER_LIMIT}`}
                            error={errors.notes}
                            onChange={(e) => onUpdate('notes', e.target.value)}
                        />

                        {errors.id?.error && (
                            <div className={local.screenError}>
                                <ScreenError
                                    name="ID"
                                    error={errors.id}
                                    clearError={clearError}
                                    allowDismiss={false}
                                />
                            </div>
                        )}
                        <div className={modal.actionButtons}>
                            <Button
                                id="darChangeEditorButton"
                                disabled={!hasRole(acceptedRoles, roles) || isButtonDisabled}
                                content="Update"
                            />
                            <Button
                                id="darChangeEditorCancelButton"
                                type="button"
                                content="Cancel"
                                onClick={onCancel}
                            />
                        </div>
                    </form>
                </div>
            </Dialog>
        </div>
    );
};

DarChangeEditor.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func,
    row: PropTypes.object
};

export default DarChangeEditor;
